import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import i18n from '../features/i18n'
import { tokenStorage } from './auth'
import { reportClientError } from './clientErrorReporter'

// 환경변수 기반 베이스 URL 주입
const BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:8501'

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

// CSRF 쿠키 읽기 유틸
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;)\\s*' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

// 요청 인터셉터 — JWT + CSRF + Accept-Language / lang 파라미터 자동 부착
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // JWT 부착
    const token = tokenStorage.getAccess()
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    // CSRF 토큰 부착 (뮤테이션 메서드에만)
    const method = (config.method ?? 'get').toLowerCase()
    if (!['get', 'head', 'options'].includes(method) && config.headers) {
      const csrfToken = getCookie('heinrich_csrf')
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken
      }
    }
    // 언어 헤더 — 백엔드가 LLM 출력 언어 결정에 활용
    const lang = (i18n.language || 'ko').split('-')[0]
    if (config.headers) {
      config.headers['Accept-Language'] = lang
      config.headers['X-Heinrich-Lang'] = lang
    }
    // LLM 계열 엔드포인트는 쿼리스트링에도 lang 추가 (기존 API 호환)
    const url = config.url ?? ''
    const llmRoutes = ['/chat', '/jsa/generate', '/image-analysis', '/tbm', '/corrective', '/risk-assessment', '/pid/demo']
    if (llmRoutes.some((r) => url.includes(r))) {
      config.params = { ...(config.params ?? {}), lang }
    }
    // 파일 다운로드(blob)는 전역 30초 타임아웃을 상향 — 서버측 변환(무료 PPTX→워터마크 PDF,
    // LibreOffice)과 대용량(수 MB) 전송이 느린 회선에서 30초를 넘겨 'Network error'로 실패하던 문제 방지.
    // (호출자가 더 긴 timeout 을 명시했으면 그대로 존중.)
    if (config.responseType === 'blob') {
      config.timeout = Math.max(config.timeout ?? 0, 120_000)
    }
    return config
  },
  (error: AxiosError) => Promise.reject(error),
)

// 응답 인터셉터 — 401 시 refresh 토큰으로 재시도
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: string) => void
  reject: (reason: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token)
    else reject(error)
  })
  failedQueue = []
}

// 401 시 refresh 재시도를 건너뛸 경로 — 인증 자체 경로에서 401 은 정상 오류
// /auth/csrf, /auth/me 는 공개 페이지(Landing 등)에서 비인증 상태로도 호출되므로
// 401 시 로그인 페이지로 강제 리다이렉트하지 않는다.
const SKIP_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/signup', '/auth/oauth/', '/auth/csrf', '/auth/me']

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const url = originalRequest?.url ?? ''
    const isAuthEndpoint = SKIP_REFRESH_PATHS.some((p) => url.includes(p))

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // 이미 갱신 중이면 큐에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
          }
          return api(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      // refresh 토큰은 HttpOnly 쿠키(saife_refresh, path=/api/auth)로만 존재하므로
      // JS(localStorage)에서 읽을 수 없다. 쿠키를 함께 보내려면 withCredentials 필요.
      // (구버전은 localStorage refresh 토큰을 찾다 항상 null → 즉시 로그아웃하는 버그였음)
      try {
        const { data } = await axios.post<{ access_token: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        )
        const newAccessToken = data.access_token
        tokenStorage.setAccess(newAccessToken)

        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
        }
        processQueue(null, newAccessToken)
        return api(originalRequest)
      } catch (refreshError) {
        // refresh 실패 — 401/403 (진짜 토큰 만료) 일 때만 강제 로그아웃.
        // 503/504/네트워크는 백엔드 일시 장애 → 토큰 유지하고 그냥 reject (사용자가 다시 시도).
        const refreshStatus = (refreshError as { response?: { status?: number } })?.response?.status
        const isRealAuthFail = refreshStatus === 401 || refreshStatus === 403
        processQueue(refreshError, null)
        if (isRealAuthFail) {
          tokenStorage.clearAll()
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // ── FastAPI 422 detail 정규화 ──
    // 검증 실패 응답의 detail 은 **객체 배열**이다:
    //   [{type,loc,msg,input,ctx}, ...]
    // 화면 코드 96곳이 하나같이 `toast.error(e?.response?.data?.detail || '...')` 로
    // 쓰기 때문에, 그대로 두면 React 가 객체를 자식으로 받아
    // "Objects are not valid as a React child" 로 **앱 전체가 흰 화면**이 된다
    // (2026-08-25 유해인자 수정에서 실제 발생). 호출부를 96군데 고치는 대신
    // 여기서 한 번 문자열로 눕힌다 — 이미 문자열이면 건드리지 않는다.
    try {
      const d = (error.response?.data as { detail?: unknown } | undefined)?.detail
      if (d != null && typeof d !== 'string') {
        const msg = Array.isArray(d)
          ? d.map((it) => (typeof it === 'string' ? it
              : (it as { msg?: string })?.msg ?? JSON.stringify(it))).join(' · ')
          : ((d as { msg?: string })?.msg ?? JSON.stringify(d))
        ;(error.response!.data as { detail?: unknown }).detail =
          msg || '요청을 처리하지 못했습니다.'
      }
    } catch { /* 정규화 실패해도 원래 흐름은 유지 */ }

    // ── 에러 모니터링: 네트워크 장애 + 서버 5xx 를 백엔드로 보고 ──
    // 4xx 는 request_log 에 서버측 기록되므로 제외(노이즈/중복 방지). 401/refresh/리포트 경로 제외.
    try {
      const st = error.response?.status
      const isReportNoise = isAuthEndpoint || url.includes('/client-errors') || st === 401
      // 폴링 엔드포인트(/health·/maintenance/status)의 '네트워크 에러'(응답 자체 없음)는 보고 제외.
      // 메일 보안 스캐너(MS SafeLinks 등) 샌드박스의 XHR 중단·모바일 회선 순단이 그대로 쌓여
      // 액션 불가능한 노이즈가 됨(2026-07-16 실측: 하루 16건 전부 봇/순단). 5xx 는 계속 보고.
      const isPollingNetworkNoise =
        st === undefined && (url === '/health' || url.startsWith('/maintenance/status'))
      if (!isReportNoise && !isPollingNetworkNoise && (st === undefined || st >= 500)) {
        const method = (originalRequest?.method ?? 'get').toUpperCase()
        reportClientError({
          kind: 'api',
          message: st ? `API ${st} ${method} ${url}` : `Network error: ${method} ${url}`,
          stack: error.stack ?? null,
          http_method: method,
          http_path: url,
          http_status: st ?? null,
          context: { code: error.code, message: error.message },
        })
      }
    } catch { /* 리포트 실패 무시 */ }

    return Promise.reject(error)
  },
)
